import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  Calendar,
  Users,
  Save,
  Loader2,
  Settings,
  Palette,
  Link,
  ExternalLink,
  Check,
  AlertCircle,
  RefreshCw,
  HelpCircle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';

// Predefined colors for calendar assignment
const CALENDAR_COLORS = [
  { name: 'Bleu', value: '#3B82F6' },
  { name: 'Rouge', value: '#EF4444' },
  { name: 'Vert', value: '#10B981' },
  { name: 'Orange', value: '#F59E0B' },
  { name: 'Violet', value: '#8B5CF6' },
  { name: 'Rose', value: '#EC4899' },
  { name: 'Cyan', value: '#06B6D4' },
  { name: 'Lime', value: '#84CC16' },
  { name: 'Indigo', value: '#6366F1' },
  { name: 'Ambre', value: '#F97316' }
];

const CalendarSettingsPage = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  
  // Form state for calendar configuration
  const [calendarForm, setCalendarForm] = useState({
    google_calendar_id: '',
    calendar_color: '#3B82F6',
    calendar_name: ''
  });

  // Master Google account calendar URL
  const [masterCalendarUrl, setMasterCalendarUrl] = useState('');
  const [savingMaster, setSavingMaster] = useState(false);

  const fetchTeamMembers = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch team members with their calendar settings from calendar endpoint
      const res = await api.get('/calendar/members');
      const members = res.data || [];
      
      // Assign default colors if not set
      const membersWithColors = members.map((m, idx) => ({
        ...m,
        calendar_color: m.calendar_color || CALENDAR_COLORS[idx % CALENDAR_COLORS.length].value
      }));
      
      setTeamMembers(membersWithColors);
    } catch (error) {
      console.error('Error fetching team members:', error);
      toast.error('Erreur lors du chargement des membres');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMasterCalendar = useCallback(async () => {
    try {
      const res = await api.get('/settings/master-calendar');
      if (res.data?.master_calendar_url) {
        setMasterCalendarUrl(res.data.master_calendar_url);
      }
    } catch (error) {
      // Settings might not exist yet
    }
  }, []);

  useEffect(() => {
    fetchTeamMembers();
    fetchMasterCalendar();
  }, [fetchTeamMembers, fetchMasterCalendar]);

  const openConfigDialog = (member) => {
    setSelectedMember(member);
    setCalendarForm({
      google_calendar_id: member.google_calendar_id || '',
      calendar_color: member.calendar_color || '#3B82F6',
      calendar_name: member.calendar_name || member.name
    });
    setShowConfigDialog(true);
  };

  const saveCalendarConfig = async () => {
    if (!selectedMember) return;
    
    setSaving(true);
    try {
      await api.put(`/calendar/members/${selectedMember.id}/calendar`, {
        member_id: selectedMember.id,
        google_calendar_id: calendarForm.google_calendar_id,
        color: calendarForm.calendar_color,
        calendar_name: calendarForm.calendar_name || selectedMember.name
      });
      
      toast.success('Configuration du calendrier enregistrée');
      setShowConfigDialog(false);
      fetchTeamMembers();
    } catch (error) {
      console.error('Error saving calendar config:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const saveMasterCalendar = async () => {
    setSavingMaster(true);
    try {
      await api.put('/settings/master-calendar', {
        master_calendar_url: masterCalendarUrl
      });
      toast.success('Calendrier principal enregistré');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSavingMaster(false);
    }
  };

  const getColorName = (colorValue) => {
    const color = CALENDAR_COLORS.find(c => c.value === colorValue);
    return color ? color.name : 'Personnalisé';
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Calendar className="w-7 h-7" style={{ color: theme?.colors?.accent }} />
              Paramètres des Calendriers
            </h1>
            <p className="text-slate-500 mt-1">
              Configurez les agendas Google pour chaque membre de l'équipe
            </p>
          </div>
          <Button variant="outline" onClick={() => setShowHelpDialog(true)}>
            <HelpCircle className="w-4 h-4 mr-2" />
            Aide
          </Button>
        </div>

        {/* Master Google Calendar */}
        <Card className="card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Compte Google Principal
            </CardTitle>
            <CardDescription>
              URL du calendrier Google principal (msc13800@gmail.com) contenant tous les agendas
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>URL du calendrier Google (iframe embed)</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  value={masterCalendarUrl}
                  onChange={(e) => setMasterCalendarUrl(e.target.value)}
                  placeholder="https://calendar.google.com/calendar/embed?src=..."
                  className="flex-1"
                />
                <Button 
                  onClick={saveMasterCalendar}
                  disabled={savingMaster}
                  style={{ backgroundColor: theme?.colors?.accent }}
                >
                  {savingMaster ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Ce calendrier sera utilisé pour afficher les créneaux disponibles aux clients
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Team Members Calendars */}
        <Card className="card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Calendriers par Membre
                </CardTitle>
                <CardDescription>
                  Assignez un calendrier Google et une couleur à chaque membre de l'équipe
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchTeamMembers}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualiser
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : teamMembers.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Aucun membre d'équipe trouvé</p>
              </div>
            ) : (
              <div className="space-y-3">
                {teamMembers.map((member) => (
                  <div 
                    key={member.id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      {/* Color indicator */}
                      <div 
                        className="w-4 h-4 rounded-full border-2"
                        style={{ 
                          backgroundColor: member.calendar_color + '40',
                          borderColor: member.calendar_color 
                        }}
                      />
                      
                      {/* Member info */}
                      <div>
                        <div className="font-medium text-slate-800">{member.name}</div>
                        <div className="text-sm text-slate-500">{member.email}</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      {/* Calendar status */}
                      {member.google_calendar_id ? (
                        <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">
                          <Check className="w-3 h-3 mr-1" />
                          Calendrier configuré
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Non configuré
                        </Badge>
                      )}

                      {/* Color badge */}
                      <Badge 
                        className="text-white"
                        style={{ backgroundColor: member.calendar_color }}
                      >
                        {getColorName(member.calendar_color)}
                      </Badge>

                      {/* Configure button */}
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => openConfigDialog(member)}
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Configurer
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Legend Preview */}
        <Card className="card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5" />
              Aperçu de la Légende
            </CardTitle>
            <CardDescription>
              Voici comment les couleurs apparaîtront dans le calendrier
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {teamMembers.filter(m => m.calendar_color).map((member) => (
                <div key={member.id} className="flex items-center gap-2">
                  <div 
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: member.calendar_color }}
                  />
                  <span className="text-sm text-slate-600">{member.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Configuration Dialog */}
        <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                Configurer le calendrier de {selectedMember?.name}
              </DialogTitle>
              <DialogDescription>
                Assignez un calendrier Google spécifique et une couleur pour ce membre
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label>Nom du calendrier</Label>
                <Input
                  value={calendarForm.calendar_name}
                  onChange={(e) => setCalendarForm({ ...calendarForm, calendar_name: e.target.value })}
                  placeholder="Ex: Agenda de Jean"
                />
              </div>

              <div>
                <Label>URL iCal du calendrier Google</Label>
                <Input
                  value={calendarForm.google_calendar_id}
                  onChange={(e) => setCalendarForm({ ...calendarForm, google_calendar_id: e.target.value })}
                  placeholder="https://calendar.google.com/calendar/ical/.../basic.ics"
                />
                <div className="text-xs text-slate-500 mt-2 space-y-1 bg-slate-50 p-2 rounded">
                  <p className="font-medium">Comment obtenir l'URL iCal :</p>
                  <ol className="list-decimal list-inside space-y-0.5">
                    <li>Ouvrez Google Calendar</li>
                    <li>Cliquez sur les 3 points à côté du calendrier</li>
                    <li>Paramètres et partage</li>
                    <li>Rendez le calendrier public</li>
                    <li>Copiez l'URL "Adresse publique au format iCal"</li>
                  </ol>
                </div>
              </div>

              <div>
                <Label>Couleur dans le calendrier</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {CALENDAR_COLORS.map((color) => (
                    <button
                      key={color.value}
                      onClick={() => setCalendarForm({ ...calendarForm, calendar_color: color.value })}
                      className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                        calendarForm.calendar_color === color.value ? 'ring-2 ring-offset-2 ring-slate-400' : ''
                      }`}
                      style={{ backgroundColor: color.value, borderColor: color.value }}
                      title={color.name}
                    />
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Couleur sélectionnée : {getColorName(calendarForm.calendar_color)}
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConfigDialog(false)}>
                Annuler
              </Button>
              <Button 
                onClick={saveCalendarConfig}
                disabled={saving}
                style={{ backgroundColor: theme?.colors?.accent }}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Help Dialog */}
        <Dialog open={showHelpDialog} onOpenChange={setShowHelpDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Comment configurer les calendriers Google</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <h3 className="font-semibold text-slate-800 mb-2">1. Créer des calendriers dans Google Calendar</h3>
                <p className="text-sm text-slate-600">
                  Dans votre compte Google (msc13800@gmail.com), créez un calendrier pour chaque membre de l'équipe.
                  Allez dans Google Calendar → Paramètres → Ajouter un calendrier → Créer un calendrier.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-slate-800 mb-2">2. Récupérer l'ID du calendrier</h3>
                <p className="text-sm text-slate-600">
                  Pour chaque calendrier, allez dans Paramètres → [Nom du calendrier] → Intégrer le calendrier.
                  L'ID du calendrier ressemble à : <code className="bg-slate-100 px-2 py-0.5 rounded">abc123xyz@group.calendar.google.com</code>
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-slate-800 mb-2">3. Partager les calendriers</h3>
                <p className="text-sm text-slate-600">
                  Assurez-vous que chaque calendrier est partagé avec les permissions appropriées :
                </p>
                <ul className="list-disc list-inside text-sm text-slate-600 mt-2">
                  <li>Public : pour permettre aux clients de voir les créneaux libres</li>
                  <li>Ou partagé avec l'équipe : pour un accès interne uniquement</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-slate-800 mb-2">4. Assigner aux membres</h3>
                <p className="text-sm text-slate-600">
                  Cliquez sur "Configurer" pour chaque membre et entrez l'ID de son calendrier Google.
                  Choisissez une couleur unique pour différencier les membres dans la vue combinée.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button onClick={() => setShowHelpDialog(false)}>Compris</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default CalendarSettingsPage;
